import { FC, useState, SyntheticEvent } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Tabs, Tab } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { SubmitHandler } from 'react-hook-form';
import { ProductFormSchema } from './schema';
import { ConfirmButton, TabPanel } from '@components';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { CustomField, JimboResponse, Product } from '@models';
import {
  useCreateProductMutation,
  useDeleteProductCustomFieldMutation,
  useDeleteProductMutation,
  useUpdateProductMutation,
} from '@services';
import { addErrorToLog } from '@store/slices/error-log';
import { showToast } from '@store/slices/notification';
import { BulkPricingTab } from './BulkPricingTab';
import { ProductForm } from './ProductForm';
import ProductImages from '../ProductImages/ProductImages';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const ProductEditModal: FC<Props> = ({ isOpen, onClose }) => {
  const product = useAppSelector((state) => state.product.value.selectedProduct);
  const [tabIndex, setTabIndex] = useState(0);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const getCustomFieldByName = (fieldName: string) => product?.custom_fields?.find((field: CustomField) => field.name === fieldName);

  const [createProduct, { isLoading: createProductIsLoading }] = useCreateProductMutation();
  const [updateProduct, { isLoading: updateProductIsLoading }] = useUpdateProductMutation();
  const [deleteProduct, { isLoading: deleteProductIsLoading }] = useDeleteProductMutation();
  const [deleteCustomField, { isLoading: deleteCustomFieldIsLoading }] = useDeleteProductCustomFieldMutation();

  const dispatch = useAppDispatch();

  const onSubmit: SubmitHandler<ProductFormSchema> = async (formData: ProductFormSchema) => {
    if (!product) return;

    // Create a new constant to hold the product payload; some fields from the form data need conversion.
    const updatedProduct: Partial<Product> = { ...(formData as unknown as Partial<Product>), custom_fields: [] };

    // Custom fields from form data need to be converted into proper custom fields for the updated product.
    for (const [key, value] of Object.entries(formData.custom_fields)) {
      const existingField = getCustomFieldByName(key) as CustomField;

      if (existingField) {
        if (value?.toString() === '') {
          // Existing field has been changed to empty and should be removed.
          try {
            await deleteCustomField({ productId: product.id, customFieldId: existingField.id });
          } catch (err) {
            dispatch(showToast({ type: 'error', message: 'Failed to update product!' }));
          }
        } else {
          // Existing field should continue to exist.
          updatedProduct.custom_fields?.push({ ...existingField, value: value?.toString() || '' });
        }
      } else if (value !== '') {
        // A new custom field should be added to the product.
        updatedProduct.custom_fields?.push({ name: key, value: value?.toString() || '' } as CustomField);
      }
    }

    // The categories in form data is an array of ProductCategory wheras the product requires an array of numbers.
    updatedProduct.categories = formData.categories?.map((category) => category.id);

    // Also add zones, as they are a subset of categories and the distinction is only made in Jimbo.
    updatedProduct.categories?.push(...formData.zones?.map((zone) => zone.id));

    // Remove any empty fields that would otherwise be included in the payload
    for (const [key, value] of Object.entries(updatedProduct)) {
      if (value === '') {
        // Type is set to `any` because it would otherwise complain about the key being a string
        // whereas that isn't the correct type on the Product interface. This is done to
        // allow for easily iterating over the keys without spelling them all out individually.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (updatedProduct as any)[key];
      }
    }

    // Check if product has an ID to determine whether it's new or existing
    if (product.id) {
      updateProduct({ ...product, ...updatedProduct })
        .unwrap()
        .then(() => {
          dispatch(showToast({ type: 'success', message: 'Successfully updated product!' }));
          onClose();
        })
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          dispatch(
            showToast({
              message: 'Failed to update product!',
              type: 'error',
            }),
          );
        });
    } else {
      // Adding a default type here because all our products are physical.
      createProduct({ ...updatedProduct, type: 'physical' })
        .unwrap()
        .then(() => {
          dispatch(showToast({ type: 'success', message: 'Successfully created new product!' }));
          onClose();
        })
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          if (error.data.message) {
            dispatch(
              showToast({
                message: error.data.message,
                type: 'error',
              }),
            );
          } else {
            dispatch(
              showToast({
                message: 'Failed to create new product!',
                type: 'error',
              }),
            );
          }
        });
    }
  };

  const handleDelete = () => {
    if (createProductIsLoading || deleteCustomFieldIsLoading || deleteProductIsLoading || updateProductIsLoading) return;

    if (product?.id) {
      deleteProduct(product.id)
        .unwrap()
        .then(() => {
          dispatch(showToast({ type: 'success', message: 'Successfully deleted product!' }));
          onClose();
        })
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          dispatch(
            showToast({
              message: 'Failed to delete product!',
              type: 'error',
            }),
          );
        });
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md" data-testid="product-edit-modal">
      <DialogTitle>{product?.name}</DialogTitle>
      <DialogContent sx={{ height: '100vh', marginTop: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabIndex} onChange={handleChange}>
            <Tab label="General" />
            <Tab label="Images" disabled={!product?.id} />
            <Tab label="Bulk Price Rules" disabled={!product?.id} />
          </Tabs>
        </Box>
        <TabPanel value={tabIndex} index={0} sx={{ pt: 3 }}>
          {product && <ProductForm onSubmit={onSubmit} product={product} />}
        </TabPanel>
        <TabPanel value={tabIndex} index={1} sx={{ pt: 3 }}>
          {product?.id && <ProductImages onClose={onClose} productId={product.id} />}
        </TabPanel>
        <TabPanel value={tabIndex} index={2} sx={{ pt: 3 }}>
          {product?.id && <BulkPricingTab onClose={onClose} product={product} />}
        </TabPanel>
      </DialogContent>
      {tabIndex === 0 && (
        <DialogActions sx={{ justifyContent: product?.id ? 'space-between' : 'flex-end', my: 2, pl: 3, pr: 5 }}>
          {product?.id && (
            <ConfirmButton
              isLoading={createProductIsLoading || deleteCustomFieldIsLoading || deleteProductIsLoading || updateProductIsLoading}
              onClick={handleDelete}
              text="Delete"
              confirmationText="Are you sure?"
              color="error"
            />
          )}
          <Box>
            <Button type="button" variant="outlined" onClick={onClose} data-tesit="product-edit-modal-cancel">
              Cancel
            </Button>
            <LoadingButton
              loading={createProductIsLoading || deleteCustomFieldIsLoading || deleteProductIsLoading || updateProductIsLoading}
              variant="contained"
              type="submit"
              data-testid="save-product-button"
              sx={{ marginLeft: 2 }}
              form="product-form"
            >
              Save Product
            </LoadingButton>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ProductEditModal;
